'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import ConcentricRing from '@/app/irl-lens/components/ConcentricRing';
import DepthPanel from '@/app/irl-lens/components/DepthPanel';
import { WARD_IRL_DATA, COLLECTIVE_CATALOG, CITY_IRL_SUMMARY, IRLExample, WardIRLData } from '@/app/irl-lens/data/irlLensData';

type Stage = 'zahir' | 'sunnat' | 'haqq' | 'amanah' | 'uboor';

const CITIES = [
  { id: 'atlanta', name: 'Atlanta', arabic: 'أَطْلَانْتَا', wards: ['west-end', 'english-vine', 'east-lake', 'bankhead', 'oakland-lakewood', 'pittsburgh-atl', 'adamsville', 'south-fulton'] },
  { id: 'chicago', name: 'Chicago', arabic: 'شِيكَاغُو', wards: ['englewood', 'south-shore', 'west-garfield', 'north-lawndale', 'austin', 'back-of-yards', 'roseland', 'chatham'] },
  { id: 'cincinnati', name: 'Cincinnati', arabic: 'سِينْسِينَاتِي', wards: ['avondale', 'over-the-rhine', 'price-hill', 'walnut-hills', 'bond-hill', 'roselawn', 'northside', 'evanston'] },
  { id: 'detroit', name: 'Detroit', arabic: 'دِيتْرُويت', wards: ['brightmoor', 'banglatown', 'east-english-village', 'mexicantown', 'north-end', 'corktown', 'hamtramck', 'southwest'] },
  { id: 'pittsburgh', name: 'Pittsburgh', arabic: 'بِيتْسْبَرْغ', wards: ['hill-district', 'homewood', 'hazelwood', 'larimer', 'east-liberty', 'garfield', 'strip-district', 'manchester'] },
  { id: 'durham', name: 'Durham', arabic: 'دَرْهَام', wards: ['hayti', 'nccu-campus', 'east-durham', 'old-north-durham', 'bragtown', 'lyon-park', 'southside', 'walltown'] },
  { id: 'louisville', name: 'Louisville', arabic: 'لُويِسْفِيل', wards: ['west-louisville', 'smoketown', 'portland-lou', 'shawnee', 'california', 'russell', 'park-hill', 'newburg'] },
  { id: 'portland', name: 'Portland', arabic: 'بُورْتْلَانْد', wards: ['alberta-arts', 'jade-district', 'cully', 'lents', 'st-johns', 'north-portland', 'east-portland', 'foster-powell'] },
  { id: 'omaha', name: 'Omaha', arabic: 'أُومَاهَا', wards: ['north-omaha', 'south-omaha', 'millard', 'benson', 'dundee', 'midtown', 'belvedere', 'florence'] },
];

const WARD_DISPLAY: Record<string, string> = {
  'west-end': 'West End / Westview', 'english-vine': 'English Ave / Vine City', 'east-lake': 'East Lake', 'bankhead': 'Bankhead / Grove Park', 'oakland-lakewood': 'Oakland City / Lakewood', 'pittsburgh-atl': 'Pittsburgh / Mechanicsville', 'adamsville': 'Adamsville / Collier Heights', 'south-fulton': 'South Fulton / College Park',
  'englewood': 'Englewood', 'south-shore': 'South Shore', 'west-garfield': 'West Garfield Park', 'north-lawndale': 'North Lawndale', 'austin': 'Austin', 'back-of-yards': 'Back of the Yards', 'roseland': 'Roseland', 'chatham': 'Chatham',
  'avondale': 'Avondale', 'over-the-rhine': 'Over-the-Rhine', 'price-hill': 'Price Hill', 'walnut-hills': 'Walnut Hills', 'bond-hill': 'Bond Hill', 'roselawn': 'Roselawn', 'northside': 'Northside', 'evanston': 'Evanston',
  'brightmoor': 'Brightmoor', 'banglatown': 'Banglatown / Conant Gardens', 'east-english-village': 'East English Village', 'mexicantown': 'Mexicantown / Vernor', 'north-end': 'North End', 'corktown': 'Corktown', 'hamtramck': 'Hamtramck', 'southwest': 'Southwest Detroit',
  'hill-district': 'Hill District', 'homewood': 'Homewood', 'hazelwood': 'Hazelwood', 'larimer': 'Larimer', 'east-liberty': 'East Liberty', 'garfield': 'Garfield', 'strip-district': 'Strip District', 'manchester': 'Manchester',
  'hayti': 'Hayti', 'nccu-campus': 'NCCU Campus / East Durham', 'east-durham': 'East Durham', 'old-north-durham': 'Old North Durham', 'bragtown': 'Bragtown', 'lyon-park': 'Lyon Park', 'southside': 'Southside', 'walltown': 'Walltown',
  'west-louisville': 'West Louisville', 'smoketown': 'Smoketown', 'portland-lou': 'Portland Neighborhood', 'shawnee': 'Shawnee', 'california': 'California', 'russell': 'Russell', 'park-hill': 'Park Hill', 'newburg': 'Newburg',
  'alberta-arts': 'Alberta Arts District', 'jade-district': 'Jade District / 82nd Ave', 'cully': 'Cully', 'lents': 'Lents', 'st-johns': 'St. Johns', 'north-portland': 'North Portland', 'east-portland': 'East Portland', 'foster-powell': 'Foster-Powell',
  'north-omaha': 'North Omaha', 'south-omaha': 'South Omaha', 'millard': 'Millard', 'benson': 'Benson', 'dundee': 'Dundee', 'midtown': 'Midtown', 'belvedere': 'Belvedere', 'florence': 'Florence',
};

const STAGE_ORDER: Stage[] = ['zahir', 'sunnat', 'haqq', 'amanah', 'uboor'];

const CATALOG_NAMES_OF_ALLAH = [
  'Ar-Razzaq', 'Al-Khaliq', 'Al-Muhyi', 'Al-Mateen', 'Al-Latif',
  'Al-Hakim', 'Al-Wali', 'Al-Adl', 'Al-Hadi', "Al-Mu\'id",
  'Al-Wahhab', 'Al-Karim', 'Al-Baqi', 'Al-Qayyum', 'Al-Fattah',
];

export default function IRLWalkthroughSection() {
  const [activeCity, setActiveCity] = useState('chicago');
  const [activeWard, setActiveWard] = useState('englewood');
  const [activeStage, setActiveStage] = useState<Stage>('zahir');
  const [activeExampleId, setActiveExampleId] = useState('fruit-tree');
  const [sessionLog, setSessionLog] = useState<IRLExample[]>([]);
  const [catalogCount, setCatalogCount] = useState(0);
  const [showCatalogForm, setShowCatalogForm] = useState(false);
  const [catalogForm, setCatalogForm] = useState({ description: '', nameOfAllah: '', notes: '' });
  const [catalogSubmitted, setCatalogSubmitted] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'left' | 'center' | 'right'>('center');

  const cityData = CITIES.find(c => c.id === activeCity);
  const wardIRLData: WardIRLData | null = WARD_IRL_DATA.find(
    d => d.wardId === activeWard && d.cityId === activeCity
  ) ?? WARD_IRL_DATA.find(d => d.wardId === activeWard) ?? null;

  const availableExamples = useMemo(() => {
    if (wardIRLData) return wardIRLData.examples;
    return WARD_IRL_DATA[0]?.examples ?? [];
  }, [wardIRLData]);

  const activeExample = availableExamples.find(e => e.id === activeExampleId) ?? availableExamples[0] ?? null;

  const handleCityChange = (cityId: string) => {
    setActiveCity(cityId);
    const city = CITIES.find(c => c.id === cityId);
    if (city) setActiveWard(city.wards[0]);
    setActiveExampleId('fruit-tree');
    setActiveStage('zahir');
  };

  const handleWardChange = (wardId: string) => {
    setActiveWard(wardId);
    setActiveExampleId('fruit-tree');
    setActiveStage('zahir');
  };

  const handleCatalogThis = () => {
    if (activeExample && !sessionLog.find(e => e.id === activeExample.id)) {
      setSessionLog(prev => [activeExample, ...prev]);
      setCatalogCount(prev => prev + 1);
    }
  };

  const handleCatalogSubmit = () => {
    setCatalogSubmitted(true);
    setCatalogCount(prev => prev + 1);
    setTimeout(() => {
      setShowCatalogForm(false);
      setCatalogSubmitted(false);
      setCatalogForm({ description: '', nameOfAllah: '', notes: '' });
    }, 2000);
  };

  const cityStats = CITY_IRL_SUMMARY.find(c => c.cityId === activeCity);
  const wardDisplayName = WARD_DISPLAY[activeWard] ?? activeWard;

  return (
    <div className="deep-green-bg">
      {/* Section header */}
      <div className="relative border-b" style={{ background: 'rgba(28,43,37,0.8)', borderColor: 'rgba(139,105,20,0.15)' }}>
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>
                THE HAAL PROJECT · INNER REALITY LENS · IRL WALKTHROUGH
              </div>
              <h2 className="font-mono text-3xl md:text-4xl tracking-tight" style={{ color: '#F5F0E8' }}>
                IRL LENS
              </h2>
              <div className="font-serif text-xl mt-1" style={{ color: 'rgba(179,138,42,0.6)' }}>عَدَسَةُ الْوَاقِع</div>
              <p className="font-mono text-xs mt-3 max-w-lg leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
                Zahir → Sunnat Allah → Haqq → Amanah → Uboor. Walk your ward. See the Ayat. Bridge to Islah.
              </p>

              {/* Adamic naming awareness — Layer 1 note */}
              <div className="mt-4 rounded-lg p-3 max-w-lg" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)' }}>
                <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.6)' }}>
                  Layer 1 — Adamic Naming
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.65 }}>
                  When you name the thing you've encountered — "This is a fruit tree," "This is a fire hydrant" — you are exercising a capacity that the heavens and the earth and the mountains did not possess. "He taught Adam the names — all of them." (Quran 2:31). The naming is the first act of Adamic inheritance. Then the fission begins.
                </p>
              </div>

              {/* Quran 6:103-104 inversion note — persistent depth reminder */}
              <div className="mt-3 rounded-lg p-3 max-w-lg" style={{ background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.2)' }}>
                <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(138,138,184,0.5)' }}>
                  Depth Reminder — Quran 6:103
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.65, fontStyle: 'italic' }}>
                  "Not grasp Him the visions but He grasp the vision." The Lens trains you to see Names in creation — but the real seeing runs the other direction. You don't see Allah through the leaf. Allah sees you through the leaf. The Names you recognize are not pieces of Allah you've grasped. They are the traces of Allah grasping you.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-widest mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>TOTAL CATALOGED</div>
                <div className="font-mono text-2xl" style={{ color: '#D4A82A' }}>{(cityStats?.totalCataloged ?? 0) + catalogCount}</div>
                <div className="font-mono text-[9px]" style={{ color: 'rgba(245,240,232,0.3)' }}>Ayat across {cityStats?.wardsActive ?? 0} wards</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile panel switcher */}
      <div className="lg:hidden flex" style={{ borderBottom: '1px solid rgba(139,105,20,0.15)' }}>
        {(['left', 'center', 'right'] as const).map((panel) => (
          <button
            key={panel}
            onClick={() => setMobilePanel(panel)}
            className="flex-1 py-2 font-mono text-[10px] tracking-widest uppercase transition-colors"
            style={{
              background: mobilePanel === panel ? 'rgba(139,105,20,0.9)' : 'transparent',
              color: mobilePanel === panel ? '#F5F0E8' : 'rgba(245,240,232,0.35)',
            }}
          >
            {panel === 'left' ? 'LOCALITY' : panel === 'center' ? 'LENS' : 'DEPTH'}
          </button>
        ))}
      </div>

      {/* Three-panel layout */}
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row min-h-[calc(100vh-200px)]">

          {/* LEFT PANEL — LOCALITY SELECTOR */}
          <div className={`lg:w-72 xl:w-80 flex-shrink-0 ${mobilePanel === 'left' ? 'block' : 'hidden lg:block'}`}
            style={{ borderRight: '1px solid rgba(44,74,62,0.4)' }}>
            <div className="sticky top-[120px] overflow-y-auto max-h-[calc(100vh-120px)]">

              {/* City tabs */}
              <div style={{ borderBottom: '1px solid rgba(44,74,62,0.3)' }}>
                <div className="font-mono text-[9px] tracking-widest px-4 pt-4 pb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>CITY</div>
                <div className="flex flex-wrap">
                  {CITIES.map(city => (
                    <button
                      key={city.id}
                      onClick={() => handleCityChange(city.id)}
                      className="py-2.5 px-2 font-mono text-[9px] tracking-widest uppercase border-b-2 transition-colors"
                      style={{
                        borderColor: activeCity === city.id ? '#B38A2A' : 'transparent',
                        color: activeCity === city.id ? '#F5F0E8' : 'rgba(245,240,232,0.3)',
                      }}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ward selector */}
              <div className="p-4" style={{ borderBottom: '1px solid rgba(44,74,62,0.3)' }}>
                <div className="font-mono text-[9px] tracking-widest mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>WARD / REGION</div>
                <div className="space-y-1">
                  {cityData?.wards.map(wardId => (
                    <button
                      key={wardId}
                      onClick={() => handleWardChange(wardId)}
                      className="w-full text-left px-3 py-2 font-mono text-[10px] tracking-wider transition-colors"
                      style={{
                        background: activeWard === wardId ? 'rgba(139,105,20,0.9)' : 'transparent',
                        color: activeWard === wardId ? '#F5F0E8' : 'rgba(245,240,232,0.4)',
                      }}
                    >
                      {WARD_DISPLAY[wardId] ?? wardId}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Encounter */}
              <div className="p-4" style={{ borderBottom: '1px solid rgba(44,74,62,0.3)' }}>
                <div className="font-mono text-[9px] tracking-widest mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>CURRENT ENCOUNTER</div>
                <div className="space-y-1">
                  {availableExamples.map(example => (
                    <button
                      key={example.id}
                      onClick={() => { setActiveExampleId(example.id); setActiveStage('zahir'); }}
                      className="w-full text-left px-3 py-2.5 transition-colors"
                      style={{
                        border: activeExampleId === example.id
                          ? '1px solid rgba(179,138,42,0.5)'
                          : '1px solid rgba(44,74,62,0.3)',
                        background: activeExampleId === example.id ? 'rgba(139,105,20,0.15)' : 'transparent',
                        color: activeExampleId === example.id ? '#F5F0E8' : 'rgba(245,240,232,0.4)',
                      }}
                    >
                      <div className="font-mono text-[10px] tracking-wider">{example.name}</div>
                      {example.arabicName && (
                        <div className="font-serif text-xs mt-0.5" style={{ color: 'rgba(179,138,42,0.5)' }}>{example.arabicName}</div>
                      )}
                      <div className="font-mono text-[9px] mt-0.5 capitalize" style={{ color: 'rgba(245,240,232,0.25)' }}>{example.category}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Session Encounter Log */}
              {sessionLog.length > 0 && (
                <div className="p-4" style={{ borderBottom: '1px solid rgba(44,74,62,0.3)' }}>
                  <div className="font-mono text-[9px] tracking-widest mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>ENCOUNTER LOG — THIS SESSION</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {sessionLog.map((ex, i) => (
                      <button
                        key={i}
                        onClick={() => { setActiveExampleId(ex.id); setActiveStage('zahir'); }}
                        className="aspect-square flex flex-col items-center justify-center p-1 transition-colors"
                        style={{ border: '1px solid rgba(44,74,62,0.4)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; }}
                      >
                        <div className="font-serif text-xs" style={{ color: 'rgba(179,138,42,0.6)' }}>{ex.arabicName?.slice(0, 4) ?? '◆'}</div>
                        <div className="font-mono text-[8px] text-center leading-tight mt-0.5" style={{ color: 'rgba(245,240,232,0.3)' }}>{ex.name.slice(0, 8)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Catalog button */}
              <div className="p-4">
                <button
                  onClick={handleCatalogThis}
                  className="w-full py-3 font-mono text-[10px] tracking-widest uppercase transition-all duration-200"
                  style={{ border: '1px solid rgba(139,105,20,0.5)', color: '#D4A82A', background: 'rgba(139,105,20,0.1)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; (e.currentTarget as HTMLElement).style.color = '#F5F0E8'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.1)'; (e.currentTarget as HTMLElement).style.color = '#D4A82A'; }}
                >
                  CATALOG THIS AYAH
                </button>
                <div className="font-mono text-[9px] text-center mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>
                  {(wardIRLData?.catalogCount ?? 0) + catalogCount} Ayat cataloged in {wardDisplayName}
                </div>
              </div>
            </div>
          </div>

          {/* CENTER PANEL — CONCENTRIC RING ENGINE */}
          <div className={`flex-1 ${mobilePanel === 'center' ? 'block' : 'hidden lg:block'}`}
            style={{ borderRight: '1px solid rgba(44,74,62,0.4)' }}>
            <div className="sticky top-[120px] p-6 lg:p-8">

              {/* Stage progress */}
              <div className="flex items-center gap-0 mb-6 overflow-x-auto">
                {STAGE_ORDER.map((stage, i) => {
                  const isActive = stage === activeStage;
                  const isPast = STAGE_ORDER.indexOf(activeStage) > i;
                  const labels: Record<Stage, string> = {
                    zahir: 'ZAHIR', sunnat: 'SUNNAT', haqq: 'HAQQ', amanah: 'AMANAH', uboor: 'UBOOR'
                  };
                  return (
                    <React.Fragment key={stage}>
                      {i > 0 && (
                        <div className="h-px flex-1 min-w-[12px] transition-colors"
                          style={{ background: isPast || isActive ? 'rgba(179,138,42,0.5)' : 'rgba(44,74,62,0.3)' }}
                        />
                      )}
                      <button
                        onClick={() => setActiveStage(stage)}
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center font-mono text-[8px] tracking-wider transition-all"
                        style={{
                          border: isActive ? '1px solid #B38A2A' : isPast ? '1px solid rgba(139,105,20,0.4)' : '1px solid rgba(44,74,62,0.3)',
                          background: isActive ? 'rgba(139,105,20,0.9)' : isPast ? 'rgba(44,74,62,0.4)' : 'transparent',
                          color: isActive ? '#F5F0E8' : isPast ? 'rgba(245,240,232,0.6)' : 'rgba(245,240,232,0.3)',
                        }}
                        title={labels[stage]}
                      >
                        {i + 1}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Active stage label */}
              <div className="text-center mb-4">
                <div className="font-mono text-[10px] tracking-widest" style={{ color: 'rgba(139,105,20,0.7)' }}>
                  {activeStage.toUpperCase()} · {activeExample?.name ?? 'SELECT ENCOUNTER'}
                </div>
              </div>

              {/* The Ring */}
              <ConcentricRing
                activeStage={activeStage}
                onStageSelect={setActiveStage}
                exampleName={activeExample?.name}
              />

              {/* Ward context */}
              <div className="mt-6 p-4 text-center" style={{ border: '1px solid rgba(44,74,62,0.35)', background: 'rgba(28,43,37,0.3)' }}>
                <div className="font-mono text-[9px] tracking-widest mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>ACTIVE WARD</div>
                <div className="font-mono text-sm" style={{ color: '#F5F0E8' }}>{wardDisplayName}</div>
                <div className="font-mono text-[9px] mt-1" style={{ color: 'rgba(179,138,42,0.5)' }}>{activeCity.toUpperCase()} · {CITIES.find(c => c.id === activeCity)?.arabic ?? ''}</div>
              </div>

              {/* Navigation arrows */}
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => {
                    const idx = STAGE_ORDER.indexOf(activeStage);
                    if (idx > 0) setActiveStage(STAGE_ORDER[idx - 1]);
                  }}
                  disabled={activeStage === 'zahir'}
                  className="font-mono text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                  style={{ color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { if (!(e.currentTarget as HTMLButtonElement).disabled) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; } }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.5)'; }}
                >
                  ← PREV
                </button>
                <button
                  onClick={() => {
                    const idx = STAGE_ORDER.indexOf(activeStage);
                    if (idx < STAGE_ORDER.length - 1) setActiveStage(STAGE_ORDER[idx + 1]);
                  }}
                  disabled={activeStage === 'uboor'}
                  className="font-mono text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                  style={{ color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { if (!(e.currentTarget as HTMLButtonElement).disabled) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; } }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.5)'; }}
                >
                  NEXT →
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL — DEPTH UNFOLDING */}
          <div className={`lg:w-[420px] xl:w-[480px] flex-shrink-0 ${mobilePanel === 'right' ? 'block' : 'hidden lg:block'}`}>
            <div className="p-6 lg:p-8 overflow-y-auto max-h-[calc(100vh-120px)] sticky top-[120px]">
              <DepthPanel
                stage={activeStage}
                example={activeExample}
                wardData={wardIRLData}
                activeCity={activeCity}
              />
            </div>
          </div>
        </div>
      </div>

      {/* DEEN SECTION — Sawm / Ramadan */}
      <div style={{ borderTop: '1px solid rgba(45,138,135,0.2)', background: 'rgba(6,13,18,0.7)' }}>
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
          <div className="mb-10">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(45,138,135,0.6)' }}>
              DEEN · دِين · SAWM / RAMADAN
            </p>
            <h2 className="font-mono text-2xl tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
              FASTING AS AN AYAH
            </h2>
            <p className="font-mono text-xs max-w-2xl leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Sawm (fasting) is not merely an act of worship — it is itself an Ayah. The body's response to restraint, the sharpening of Basirah, the collective rhythm of Ramadan — all are signs pointing back to Al-Qadir and Ar-Razzaq.
            </p>
          </div>

          {/* Foundational verses — 2:183-186 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {[
              {
                ref: '2:183',
                arabic: 'يَا أَيُّهَا الَّذِينَ آمَنُوا كُتِبَ عَلَيْكُمُ الصِّيَامُ كَمَا كُتِبَ عَلَى الَّذِينَ مِن قَبْلِكُمْ لَعَلَّكُمْ تَتَّقُونَ',
                translation: 'O you who believe, fasting has been prescribed for you as it was prescribed for those before you — so that you may attain Taqwah.',
                note: 'The purpose of Sawm is Taqwah — the same quality the IRL Lens cultivates. Fasting is a Taqwah-training methodology.',
                color: '#2d8a87',
                href: 'https://quran.com/2/183',
              },
              {
                ref: '2:185',
                arabic: 'شَهْرُ رَمَضَانَ الَّذِي أُنزِلَ فِيهِ الْقُرْآنُ هُدًى لِّلنَّاسِ وَبَيِّنَاتٍ مِّنَ الْهُدَىٰ وَالْفُرْقَانِ',
                translation: 'The month of Ramadan in which the Quran was revealed — guidance for the people and clear proofs of guidance and the Furqan (criterion).',
                note: 'Ramadan is the month of Furqan — the sharpening of discernment. The caretaker who fasts emerges with a clearer lens.',
                color: '#D4A82A',
                href: 'https://quran.com/2/185',
              },
              {
                ref: '2:186',
                arabic: 'وَإِذَا سَأَلَكَ عِبَادِي عَنِّي فَإِنِّي قَرِيبٌ ۖ أُجِيبُ دَعْوَةَ الدَّاعِ إِذَا دَعَانِ',
                translation: 'And when My servants ask you about Me — indeed I am near. I respond to the call of the caller when he calls upon Me.',
                note: 'The proximity verse — placed in the middle of the Sawm verses. Fasting creates the conditions for nearness. The lens sharpens when the body is stilled.',
                color: '#2d7a47',
                href: 'https://quran.com/2/186',
              },
              {
                ref: '2:184',
                arabic: 'وَأَن تَصُومُوا خَيْرٌ لَّكُمْ ۖ إِن كُنتُمْ تَعْلَمُونَ',
                translation: 'And that you fast is better for you — if only you knew.',
                note: 'The conditional: "if only you knew" — Yaqeen is the fruit of Sawm. The caretaker who fasts with awareness arrives at certainty.',
                color: '#8a8ab8',
                href: 'https://quran.com/2/184',
              },
            ].map((v, i) => (
              <div key={i} className="p-5 rounded-lg" style={{ background: 'rgba(6,13,18,0.5)', border: `1px solid ${v.color}20`, borderTop: `2px solid ${v.color}50` }}>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: v.color, direction: 'rtl', textAlign: 'right', lineHeight: 1.8, marginBottom: 8 }}>
                  {v.arabic}
                </p>
                <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, fontStyle: 'italic', marginBottom: 8 }}>
                  "{v.translation}"
                </p>
                <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
                  {v.note}
                </p>
                <a
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[10px] tracking-widest"
                  style={{ color: `${v.color}80` }}
                >
                  Quran {v.ref} ↗
                </a>
              </div>
            ))}
          </div>

          {/* Sawm as IRL Lens encounter */}
          <div className="p-6 rounded-xl" style={{ background: 'rgba(45,138,135,0.05)', border: '1px solid rgba(45,138,135,0.15)' }}>
            <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(45,138,135,0.6)' }}>
              SAWM THROUGH THE IRL LENS
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { stage: 'ZAHIR', ar: 'ظَاهِر', desc: 'The observable: the body fasting, the community gathering for Iftar, the Masjid full at Tarawih. The Zahir of Ramadan is visible in every Muslim neighborhood.', color: '#D4A82A' },
                { stage: 'SUNNAT ALLAH', ar: 'سُنَّة اللَّه', desc: 'The pattern: restraint produces clarity. Every tradition of fasting — across all peoples — follows this Sunnat. The body\'s wisdom is a sign of Al-Hakim.', color: '#2d7a47' },
                { stage: 'HAQQ → AMANAH', ar: 'حَقّ → أَمَانَة', desc: 'The truth: Sawm is Amanah — the body held in trust, the community\'s rhythm honored, the Quran\'s revelation commemorated. Fasting is the fulfillment of a trust.', color: '#2d8a87' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-lg" style={{ background: 'rgba(6,13,18,0.4)', border: `1px solid ${item.color}15` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: item.color }}>{item.stage}</span>
                    <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${item.color}80`, direction: 'rtl' }}>{item.ar}</span>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* COLLECTIVE LENS SECTION */}
      <div style={{ borderTop: '1px solid rgba(139,105,20,0.15)', background: 'rgba(28,43,37,0.5)' }}>
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <div className="font-mono text-[10px] tracking-widest mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>THE COLLECTIVE LENS</div>
              <h2 className="font-mono text-2xl tracking-tight" style={{ color: '#F5F0E8' }}>CARETAKER NETWORK</h2>
              <p className="font-mono text-xs mt-2 max-w-lg leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
                A growing catalog of Ayat encountered by caretakers across wards and cities. Each entry strengthens the collective recognition of Allah&apos;s Names in creation.
              </p>
            </div>
            <div className="font-mono text-[10px] text-right" style={{ color: 'rgba(139,105,20,0.6)' }}>
              <div className="text-2xl" style={{ color: '#D4A82A' }}>{448 + catalogCount}</div>
              <div>Ayat cataloged across 72 wards in 9 cities</div>
              <div className="mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>Alhamdulilah</div>
            </div>
          </div>

          {/* Recent catalog grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-8">
            {COLLECTIVE_CATALOG.map((entry) => (
              <div
                key={entry.id}
                className="p-3 transition-colors cursor-pointer"
                style={{ border: '1px solid rgba(44,74,62,0.35)', background: 'rgba(28,43,37,0.4)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.35)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.35)'; }}
              >
                <div className="font-serif text-base mb-1" style={{ color: 'rgba(179,138,42,0.7)' }}>{entry.arabicName}</div>
                <div className="font-mono text-[9px] tracking-wider" style={{ color: 'rgba(245,240,232,0.5)' }}>{entry.nameOfAllah}</div>
                <div className="font-mono text-[8px] mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{entry.ayahType}</div>
                <div className="font-mono text-[8px] mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>{entry.wardName}</div>
                <div className="font-mono text-[8px] mt-1 capitalize" style={{ color: 'rgba(245,240,232,0.15)' }}>{entry.cityId}</div>
                <div className="font-mono text-[8px] mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>{entry.timestamp}</div>
              </div>
            ))}
          </div>

          {/* Submit form */}
          <div className="p-6 lg:p-8" style={{ border: '1px solid rgba(44,74,62,0.4)', background: 'rgba(28,43,37,0.3)' }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] tracking-widest mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>SUBMIT AN ENCOUNTER</div>
                <h3 className="font-mono text-lg tracking-wide" style={{ color: '#F5F0E8' }}>CATALOG FISABILILLAH</h3>
              </div>
              <button
                onClick={() => setShowCatalogForm(!showCatalogForm)}
                className="font-mono text-[10px] tracking-widest px-4 py-2 transition-colors"
                style={{ border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.5)' }}
                onMouseEnter={(e) => { if (!(e.currentTarget as HTMLButtonElement).disabled) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; } }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.5)'; }}
              >
                {showCatalogForm ? 'CLOSE' : 'OPEN FORM'}
              </button>
            </div>

            {showCatalogForm && (
              <div className="space-y-4">
                {catalogSubmitted ? (
                  <div className="text-center py-8">
                    <div className="font-serif text-3xl mb-3" style={{ color: 'rgba(179,138,42,0.7)' }}>الْحَمْدُ لِلَّه</div>
                    <div className="font-mono text-sm" style={{ color: 'rgba(245,240,232,0.6)' }}>Encounter cataloged fisabilillah.</div>
                    <div className="font-mono text-xs mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>Jazakallahu khayran.</div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="font-mono text-[9px] tracking-widest block mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>DESCRIBE THE ENCOUNTER</label>
                      <textarea
                        value={catalogForm.description}
                        onChange={e => setCatalogForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="What did you encounter? Where? What did you observe?"
                        className="w-full p-3 font-mono text-xs focus:outline-none resize-none h-24"
                        style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.7)' }}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[9px] tracking-widest block mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>NAME OF ALLAH IT SURFACES</label>
                        <select
                          value={catalogForm.nameOfAllah}
                          onChange={e => setCatalogForm(f => ({ ...f, nameOfAllah: e.target.value }))}
                          className="w-full p-3 font-mono text-xs focus:outline-none"
                          style={{ background: '#1C2B25', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.7)' }}
                        >
                          <option value="">Select a Name...</option>
                          {CATALOG_NAMES_OF_ALLAH.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="font-mono text-[9px] tracking-widest block mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>WARD</label>
                        <select
                          className="w-full p-3 font-mono text-xs focus:outline-none"
                          style={{ background: '#1C2B25', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.7)' }}
                        >
                          <option value="">Select ward...</option>
                          {cityData?.wards.map(w => (
                            <option key={w} value={w}>{WARD_DISPLAY[w] ?? w}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[9px] tracking-widest block mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>NOTES (OPTIONAL)</label>
                      <textarea
                        value={catalogForm.notes}
                        onChange={e => setCatalogForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Any additional observations, reflections, or connections..."
                        className="w-full p-3 font-mono text-xs focus:outline-none resize-none h-16"
                        style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.7)' }}
                      />
                    </div>
                    <button
                      onClick={handleCatalogSubmit}
                      className="w-full md:w-auto px-8 py-3 font-mono text-[10px] tracking-widest uppercase transition-all duration-200"
                      style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
                    >
                      CATALOG FISABILILLAH
                    </button>
                  </>
                )}
              </div>
            )}

            {!showCatalogForm && (
              <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.3)' }}>
                Document an Ayah you&apos;ve encountered in your ward. Your catalog entry joins a growing network of caretakers doing Islah of their localities while consciously learning about the Sunnat of Allah.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* NAVIGATION BRIDGE */}
      <div style={{ borderTop: '1px solid rgba(139,105,20,0.15)', background: 'rgba(28,43,37,0.4)' }}>
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link
              href="/ayat-in-creation"
              className="font-mono text-[10px] tracking-widest px-6 py-3 transition-colors"
              style={{ color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.4)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
            >
              ← AYAT IN CREATION
            </Link>
            <div className="text-center">
              <div className="font-mono text-[10px] tracking-widest mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>THE HAAL PROJECT</div>
              <div className="font-serif text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>Recognition becomes Caretaking</div>
              <div className="font-mono text-[9px] mt-1" style={{ color: 'rgba(245,240,232,0.25)' }}>The Ayah you&apos;ve seen calls for action — find the resources, bridge the need, serve fisabilillah.</div>
            </div>
            <Link
              href="/muslim-resource-guide"
              className="font-mono text-[10px] tracking-widest px-6 py-3 transition-all duration-200"
              style={{ color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)', background: 'rgba(139,105,20,0.2)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.2)'; }}
            >
              MUSLIM RESOURCE GUIDE →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
